import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { sectionBadge, fadeUp } from '../../utils/animationUtils';
import { SiHackerrank, SiFreecodecamp } from 'react-icons/si';

gsap.registerPlugin(ScrollTrigger);

// ── Custom logo components for issuers without react-icons ──────────────────
const DicodingLogo = () => (
    <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'linear-gradient(135deg, #2D6CDF, #1B4F9E)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid #111', flexShrink: 0,
    }}>
        <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 16, color: '#fff' }}>D</span>
    </div>
);

const CourseNetLogo = () => (
    <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'linear-gradient(135deg, #E94235, #C62828)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid #111', flexShrink: 0,
    }}>
        <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 11, color: '#fff' }}>CN</span>
    </div>
);

// ── Certification data ──────────────────────────────────────────────────────
const certifications = [
    {
        name: "SQL (Advanced)",
        issuer: "HackerRank",
        date: "Jan 2025",
        icon: <SiHackerrank size={20} />,
        iconBg: '#00EA64',
        iconColor: '#111',
        skills: ["Database Management System (DBMS)", "Full-Stack Development"],
    },
    {
        name: "Problem Solving (Intermediate)",
        issuer: "HackerRank",
        date: "Jan 2025",
        icon: <SiHackerrank size={20} />,
        iconBg: '#00EA64',
        iconColor: '#111',
        skills: ["Database Management System (DBMS)", "Full-Stack Development"],
    },
    {
        name: "Menjadi Javascript Engineer",
        issuer: "Course-Net Indonesia",
        date: "Nov 2022",
        icon: <CourseNetLogo />,
        iconBg: null,
        iconColor: null,
        skills: ["JavaScript", "Node.js"],
    },
    {
        name: "Belajar Dasar Pemrograman Web",
        issuer: "Dicoding Indonesia",
        date: "Oct 2022",
        icon: <DicodingLogo />,
        iconBg: null,
        iconColor: null,
        skills: ["Full-Stack Development", "Next.js"],
    },
    {
        name: "Belajar Dasar Pemrograman JavaScript",
        issuer: "Dicoding Indonesia",
        date: "Oct 2022",
        icon: <DicodingLogo />,
        iconBg: null,
        iconColor: null,
        skills: ["Full-Stack Development", "JavaScript"],
    },
    {
        name: "Responsive Web Design",
        issuer: "freeCodeCamp",
        date: "Sep 2022",
        icon: <SiFreecodecamp size={20} />,
        iconBg: '#0A0A23',
        iconColor: '#fff',
        skills: ["Full-Stack Development", "Next.js"],
    },
];

// ── Component ───────────────────────────────────────────────────────────────
const CertificationsSection = () => {
    const containerRef = useRef(null);
    const ghostRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set(['.cert-badge', '.cert-heading'], { opacity: 0 });
            gsap.set('.cert-card', { opacity: 0, y: 30 });

            sectionBadge('.cert-badge');
            fadeUp('.cert-heading');

            gsap.to('.cert-card', {
                opacity: 1, y: 0, stagger: 0.08, duration: 0.6, ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.cert-card',
                    start: 'top 88%',
                    toggleActions: 'play none none reverse',
                    fastScrollEnd: true,
                },
            });

            // Ghost text parallax
            if (ghostRef.current) {
                gsap.to(ghostRef.current, {
                    x: -60, ease: 'none',
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top bottom', end: 'bottom top',
                        scrub: 1,
                    },
                });
            }

            // Card hover
            gsap.utils.toArray('.cert-card').forEach(card => {
                card.addEventListener('mouseenter', () => {
                    gsap.to(card, { y: -4, boxShadow: '6px 6px 0 #111', duration: 0.2, ease: 'power2.out' });
                });
                card.addEventListener('mouseleave', () => {
                    gsap.to(card, { y: 0, boxShadow: '4px 4px 0 #111', duration: 0.4, ease: 'elastic.out(1,0.5)' });
                });
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="certifications"
            ref={containerRef}
            className="py-24 font-jakarta bg-[#F2EFE9] overflow-hidden relative"
        >
            {/* Ghost Watermark */}
            <span ref={ghostRef} className="ghost-text" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>CERTS</span>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                {/* Header */}
                <div className="mb-14">
                    <div
                        className="cert-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full mb-5"
                        style={{ boxShadow: '3px 3px 0 #111', fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}
                    >
                        <span className="w-2 h-2 rounded-full bg-[#FFE600] inline-block" />
                        CERTIFICATIONS
                    </div>

                    <h2
                        className="cert-heading"
                        style={{
                            fontFamily: "'Syne', sans-serif", fontWeight: 800,
                            fontSize: 'var(--text-h1)', letterSpacing: '-0.03em',
                            lineHeight: 1.0, color: '#111',
                        }}
                    >
                        Validated{' '}
                        <span style={{ color: '#FF3366', fontStyle: 'italic' }}>Skills.</span>
                    </h2>
                </div>

                {/* Cert Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {certifications.map((cert, i) => (
                        <div
                            key={i}
                            className="cert-card"
                            style={{
                                background: '#fff',
                                border: '3px solid #111',
                                borderRadius: 14,
                                padding: 'clamp(18px, 2vw, 24px)',
                                boxShadow: '4px 4px 0 #111',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 14,
                                cursor: 'default',
                            }}
                        >
                            {/* Top: icon + issuer + date */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {/* Icon */}
                                {cert.iconBg ? (
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 8,
                                        backgroundColor: cert.iconBg, color: cert.iconColor,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: '2px solid #111', flexShrink: 0,
                                    }}>
                                        {cert.icon}
                                    </div>
                                ) : (
                                    cert.icon
                                )}

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{
                                        fontFamily: "'Space Mono', monospace",
                                        fontSize: 10, fontWeight: 700,
                                        color: '#888', textTransform: 'uppercase',
                                        letterSpacing: '0.12em', lineHeight: 1,
                                    }}>
                                        {cert.issuer}
                                    </p>
                                    <p style={{
                                        fontFamily: "'Space Mono', monospace",
                                        fontSize: 9, fontWeight: 600,
                                        color: '#bbb', letterSpacing: '0.08em',
                                        marginTop: 2,
                                    }}>
                                        {cert.date}
                                    </p>
                                </div>
                            </div>

                            {/* Name */}
                            <h3 style={{
                                fontFamily: "'Syne', sans-serif",
                                fontWeight: 700, fontSize: 'clamp(14px, 1.4vw, 17px)',
                                color: '#111', lineHeight: 1.25,
                                letterSpacing: '-0.01em',
                            }}>
                                {cert.name}
                            </h3>

                            {/* Skill tags */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 'auto' }}>
                                {cert.skills.map(skill => (
                                    <span
                                        key={skill}
                                        style={{
                                            fontFamily: "'Space Mono', monospace",
                                            fontSize: 8, fontWeight: 700,
                                            color: '#555', textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            padding: '3px 8px',
                                            border: '1.5px solid #ddd',
                                            borderRadius: 4,
                                        }}
                                    >
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CertificationsSection;
